<template>
  <div class="mb-5">
    <router-link
      :to="'/agreement/' + route.params.agid + '/appointments'"
      class="inline-flex items-center hover:text-primary"
    >
      <Icon
        type="chevron-left"
        class="mr-1"
      />
      <span>{{ $t('ui.entities.appointment.label', 2) }}</span>
    </router-link>
  </div>
  <div v-if="appointmentData">
    <Alert
      v-if="appointmentIsLocked"
      severity="info"
      icon="lock"
      title="Locked"
      :message="appointmentIsLocked"
      :show-dismiss-button="false"
      :show-close-button="false"
      class="mb-4"
    />
    <Card>
      <div class="flex flex-col md:flex-row">
        <CardBody
          class="border-b md:border-b-0 md:border-r border-secondary flex flex-col gap-4 items-center flex-shrink-0"
        >
          <Chip
            v-if="appointmentData.customerIsAttending != null"
            v-tippy="'Customer confirmed via SMS'"
            size="md"
            icon="user-check"
            :severity="appointmentData.customerIsAttending ? 'success' : 'danger'"
          >
            <span>{{ appointmentData.customerIsAttending ? 'Customer Attending' : 'Customer Not Attending' }}</span>
          </Chip>
          <DateTile
            :date="appointmentData.time"
            design="minimal"
            :show-time="true"
            :class="{ 'text-secondary': appointmentData?.isCancelled }"
          />
          <div
            v-if="appointmentData.timeZone != appointmentData.customerTimeZone"
            class="text-secondary text-center"
          >
            {{ appointmentData.timeZone }} time
          </div>
          <Chip
            size="md"
            :icon="
              appointmentData.method ? getDictionaryItem('appointmentMethod', appointmentData.method)?.icon : 'question'
            "
          >
            {{
              $t(
                appointmentData.method
                  ? getDictionaryItem('appointmentMethod', appointmentData.method)?.localeKey
                  : 'Unknown Method'
              )
            }}
          </Chip>
        </CardBody>

        <div class="flex-1">
          <CardBody class="border-b border-secondary">
            <div class="flex flex-col w-full items-start md:justify-between md:flex-row mb-4 md:mb-2">
              <div>
                <div class="flex items-center gap-2">
                  <h3
                    class="mb-4 md:mb-0 font-semibold"
                    :class="[`text-${getDictionaryItem('appointmentStatus', appointmentData?.status)?.color}`]"
                  >
                    {{ $t(getDictionaryItem('appointmentStatus', appointmentData?.status)?.localeKey) }}
                  </h3>

                  <div v-if="appointmentData?.isCancelled">
                    <div>
                      <span class="text-secondary mr-1 lowercase">{{ $t('ui.common.by') }}</span>
                      <span
                        v-if="appointmentData.cancelledBySource != 'customer' && appointmentData.cancelledBy"
                        v-tippy="{
                          content: `<span class='capitalize'>${appointmentData.cancelledBySource}</span>`,
                          allowHTML: true
                        }"
                        class="text-secondary"
                      >
                        {{ appointmentData.cancelledBy.forename + ' ' + appointmentData.cancelledBy.surname }}
                      </span>
                      <span
                        v-else
                        class="text-secondary"
                      >
                        {{ appointmentData.cancelledBySource }}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="appointmentData?.cancelledReason"
                  class="my-2"
                >
                  <q>{{ appointmentData.cancelledReason }}</q>
                </div>
              </div>

              <div class="flex flex-col w-full md:w-auto sm:flex-row gap-2">
                <div class="relative">
                  <Select
                    v-model="appointmentData.assignedTo"
                    :readonly="!$can('assign_appointments') || appointmentData?.isCancelled || appointmentIsLocked"
                    mode="single"
                    label-key="fullName"
                    value-key="id"
                    class="min-w-[160px]"
                    :placeholder="$t('ui.actions.select')"
                    :close-on-select="true"
                    :searchable="true"
                    on-select-return-as-object
                    :options="assignableUsers.data.value"
                    :loading="appointmentAssigning"
                    @select="updateAssignedUser"
                  />
                  <div
                    v-if="appointmentData.assignedTo"
                    class="absolute -top-2 left-2 text-xs rounded-full bg-white dark:bg-darkmode-1 text-secondary px-1"
                  >
                    {{ $t('ui.entities.appointment.assignedTo') }}
                  </div>
                </div>

                <div class="md:ml-4 flex gap-2 flex-grow">
                  <Button
                    v-tippy="{ content: $t('ui.actions.edit') }"
                    severity="primary"
                    outlined
                    icon="pencil"
                    :disabled="appointmentIsLocked"
                    class="flex-grow"
                    @click="rescheduleClick"
                  />
                  <Button
                    v-tippy="{ content: $t('ui.entities.appointment.reschedule') }"
                    severity="secondary"
                    outlined
                    icon="calendar-clock"
                    :disabled="appointmentData?.isCancelled || appointmentIsLocked"
                    class="flex-grow"
                    @click="rescheduleClick"
                  />
                  <Button
                    v-tippy="{ content: $t('ui.actions.cancel') }"
                    severity="danger"
                    outlined
                    icon="ban"
                    :disabled="appointmentIsLocked"
                    class="flex-grow"
                    @click="cancelClick"
                  />
                  <Button
                    v-if="$can('delete_appointments')"
                    v-tippy="{ content: $t('ui.actions.delete') }"
                    severity="danger"
                    outlined
                    icon="trash"
                    class="flex-grow"
                    @click="deleteClick"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="appointmentDetailsCollapsable"
              class="inline-flex text-secondary items-center gap-1 cursor-pointer link-hover"
              :class="{ 'mb-2': appointmentDetailsVisible }"
              @click="appointmentDetailsVisible = !appointmentDetailsVisible"
            >
              <div>{{ appointmentDetailsVisible ? 'Hide' : 'Show' }} Details</div>
              <Icon :type="appointmentDetailsVisible ? 'chevron-up' : 'chevron-down'" />
            </div>

            <div v-if="appointmentDetailsVisible">
              <div class="flex items-center mb-2">
                <Icon
                  type="location-dot"
                  class="mr-3"
                />
                <span>{{ appointmentData.dealershipName }}</span>
              </div>
              <div
                v-if="appointmentData.intent"
                v-tippy="{ content: 'Intent' }"
                class="inline-block mb-2"
              >
                <Icon
                  type="thought-bubble"
                  class="mr-3"
                />
                <span>{{ appointmentData.intent }}</span>
              </div>
              <div class="flex items-center mb-2">
                <Icon
                  type="globe-pointer"
                  class="mr-3"
                />
                <CopyText>
                  {{ appointmentData.selfServeUrl }}
                </CopyText>
              </div>
              <div>
                <div class="flex items-baseline">
                  <Icon
                    type="note"
                    class="mr-3"
                  />
                  <p>{{ appointmentData.note ? appointmentData.note : 'No note left' }}</p>
                </div>
              </div>
            </div>
          </CardBody>

          <CardBody>
            <div v-if="appointmentData?.outcome.decision">
              <div class="flex justify-between items-center">
                <h3>
                  {{ $t(getDictionaryItem('appointmentDecision', appointmentData.outcome.decision)?.label) }}
                </h3>
                <div>
                  <Button
                    v-tippy="{
                      content: $t('ui.crud.update', { item: $t('ui.entities.appointment.outcome') })
                    }"
                    severity="secondary"
                    outlined
                    icon="pencil"
                    size="sm"
                    :label="$t('ui.crud.update', { item: $t('ui.entities.appointment.outcome.outcome') })"
                    :disabled="appointmentIsLocked"
                    @click="outcomeModalIsOpen = true"
                  />
                </div>
              </div>

              <div
                v-if="appointmentData.outcome.reason"
                class="mb-2"
              >
                <!-- <Label no-margin>{{ $t('ui.common.note') }}</Label> -->
                <div>
                  <q>{{ appointmentData.outcome.reason }}</q>
                </div>
              </div>
              <div
                v-if="appointmentData.outcome.decision == 'refinanced'"
                class="mb-2"
              >
                <div class="flex gap-2 max-w-[400px] mt-2">
                  <div class="details-grid-box flex-1">
                    <div class="details-grid-box-title">{{ $t('ui.common.amount') }}</div>
                    <div class="details-grid-box-data capitalize">
                      {{
                        formatNumber(
                          appointmentData.outcome.refinance.amountRefinanced,
                          'currency',
                          0,
                          appointmentData.outcome.currency
                        )
                      }}
                    </div>
                  </div>
                  <div class="details-grid-box flex-1">
                    <div class="details-grid-box-title">{{ $t('ui.common.until') }}</div>
                    <div class="details-grid-box-data">
                      {{
                        appointmentData.outcome.refinance.refinancedUntil
                          ? dayjs(appointmentData.outcome.refinance.refinancedUntil).format('LL')
                          : '-'
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="appointmentData.outcome.decision == 'part_exchanged' && appointmentData.outcome.newVehicle.type"
              >
                <Label no-margin>{{ $t('ui.entities.vehicle.newVehicle') }}</Label>
                <div class="flex gap-2 max-w-[400px]">
                  <div class="details-grid-box flex-1">
                    <div class="details-grid-box-title">{{ $t('ui.common.type') }}</div>
                    <div class="details-grid-box-data capitalize">
                      {{ appointmentData.outcome.newVehicle.type }}
                    </div>
                  </div>
                  <div class="details-grid-box flex-1">
                    <div class="details-grid-box-title">{{ $t('ui.common.value') }}</div>
                    <div class="details-grid-box-data">
                      {{
                        formatNumber(
                          appointmentData.outcome.newVehicle.value,
                          'currency',
                          0,
                          appointmentData.outcome.currency
                        )
                      }}
                    </div>
                  </div>
                  <div class="details-grid-box flex-1">
                    <div class="details-grid-box-title">Est. Delivery</div>
                    <div class="details-grid-box-data">
                      {{ dayjs(appointmentData.outcome.newVehicle.estimatedDeliveryDate).format("D MMM 'YY") }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="$can('outcome_appointments') && !appointmentData.outcome.decision"
              v-tippy="{
                content: !isActionable ? 'Available closer to the appointment time' : null
              }"
              class="inline"
            >
              <Button
                :label="$t('ui.entities.appointment.outcome.setOutcome')"
                :disabled="!isActionable || appointmentData?.isCancelled"
                icon="check"
                @click="outcomeModalIsOpen = true"
              />
            </div>
          </CardBody>
        </div>
      </div>
    </Card>

    <div class="grid md:grid-cols-2 md:gap-4">
      <!-- TODO: Add functionality to add/display files once returned by API -->
      <div>
        <Card>
          <CardBody>
            <CardTitle :title="$t('ui.entities.appointment.checklist.label')" />
            <ListGroup>
              <ListGroupItem>
                <div>
                  <div class="flex items-center justify-between">
                    <div>{{ $t('ui.entities.appointment.checklist.settlementFigureGiven') }}</div>
                    <Checkbox
                      v-model="appChecklist.settlementFigureGiven"
                      size="md"
                      no-margin
                      :readonly="!!appointmentIsLocked"
                    />
                  </div>
                  <div v-if="appChecklist.settlementFigureGiven">
                    <Separator class="my-4" />
                    <div>
                      <div class="mt-4">
                        <div class="flex justify-between">
                          <div>
                            <Input
                              v-model="appChecklist.settlementAmount"
                              :readonly="!!appointmentIsLocked"
                              :icon="appointmentData.outcome.currency.toLowerCase()"
                              class="w-[120px]"
                              :has-error="vChecklist$.settlementAmount.$error"
                            />
                            <InputError :has-error="vChecklist$.settlementAmount.$error">
                              {{ vChecklist$.settlementAmount.$errors[0]?.$message }}
                            </InputError>
                          </div>
                          <div>
                            <Button
                              icon="cloud-upload"
                              outlined
                              severity="secondary"
                              @click="uploadChecklistFileClick('settlement')"
                            />
                          </div>
                        </div>
                        <div
                          v-for="file in appointmentData.checklist.settlementFiles"
                          :key="file"
                          class="flex justify-end mt-1 hover:text-primary cursor-pointer"
                          @click="openFileInNewTab(file)"
                        >
                          <Icon
                            type="file"
                            class="mr-1"
                          />
                          <span>{{ file.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ListGroupItem>
            </ListGroup>
            <ListGroup class="mt-2">
              <ListGroupItem>
                <div class="flex justify-between">
                  <div>{{ $t('ui.entities.appointment.checklist.vehicleValued') }}</div>
                  <Checkbox
                    v-model="appChecklist.vehicleValued"
                    no-margin
                    :readonly="!!appointmentIsLocked"
                  />
                </div>
                <div v-if="appChecklist.vehicleValued">
                  <Separator class="my-4" />
                  <div>
                    <Input
                      v-model="appChecklist.valuationAmount"
                      :readonly="!!appointmentIsLocked"
                      :icon="appointmentData.outcome.currency.toLowerCase()"
                      class="w-[120px]"
                      :has-error="vChecklist$.valuationAmount.$error"
                    />
                    <InputError :has-error="vChecklist$.valuationAmount.$error">
                      {{ vChecklist$.valuationAmount.$errors[0]?.$message }}
                    </InputError>
                  </div>
                </div>
              </ListGroupItem>
            </ListGroup>
            <ListGroup
              v-if="appointment.data.value.method == 'physical'"
              class="mt-2"
            >
              <ListGroupItem>
                <div class="flex justify-between">
                  <div>{{ $t('ui.entities.appointment.checklist.testDriveCompleted') }}</div>
                  <Checkbox
                    v-model="appChecklist.testDriveCompleted"
                    no-margin
                    :readonly="!!appointmentIsLocked"
                  />
                </div>
              </ListGroupItem>
            </ListGroup>
            <ListGroup class="mt-2">
              <ListGroupItem>
                <div class="flex justify-between">
                  <div>{{ $t('ui.entities.appointment.checklist.newVehicleOffersProvided') }}</div>

                  <Checkbox
                    v-model="appChecklist.newCarOffersProvided"
                    no-margin
                    :readonly="!!appointmentIsLocked"
                  />
                </div>
                <div v-if="appChecklist.newCarOffersProvided">
                  <Separator class="my-4" />
                  <div class="flex justify-end">
                    <Button
                      icon="cloud-upload"
                      outlined
                      severity="secondary"
                      @click="uploadChecklistFileClick('newCarOffers')"
                    />
                  </div>
                  <div
                    v-for="file in appointmentData.checklist.newCarOfferFiles"
                    :key="file"
                    class="flex justify-end mt-1 hover:text-primary cursor-pointer"
                    @click="openFileInNewTab(file)"
                  >
                    <Icon
                      type="file"
                      class="mr-1"
                    />
                    <span>{{ file.name }}</span>
                  </div>
                </div>
              </ListGroupItem>
            </ListGroup>
            <ListGroup class="mt-2">
              <ListGroupItem>
                <div class="flex justify-between">
                  <div>{{ $t('ui.entities.appointment.checklist.refinanceQuoteGiven') }}</div>
                  <Checkbox
                    v-model="appChecklist.refinanceQuoteGiven"
                    no-margin
                    :readonly="!!appointmentIsLocked"
                  />
                </div>

                <div v-if="appChecklist.refinanceQuoteGiven">
                  <Separator class="my-4" />
                  <div class="flex justify-between">
                    <div>
                      <Input
                        v-model="appChecklist.refinanceQuoteAmount"
                        :readonly="!!appointmentIsLocked"
                        :icon="appointmentData.outcome.currency.toLowerCase()"
                        class="w-[120px]"
                        :has-error="vChecklist$.refinanceQuoteAmount.$error"
                      />
                      <InputError :has-error="vChecklist$.refinanceQuoteAmount.$error">
                        {{ vChecklist$.refinanceQuoteAmount.$errors[0]?.$message }}
                      </InputError>
                    </div>
                    <div>
                      <Button
                        icon="cloud-upload"
                        outlined
                        severity="secondary"
                        @click="uploadChecklistFileClick('refinanceQuote')"
                      />
                    </div>
                  </div>
                  <div
                    v-for="file in appointmentData.checklist.refinanceQuoteFiles"
                    :key="file"
                    class="flex justify-end mt-1 hover:text-primary cursor-pointer"
                    @click="openFileInNewTab(file)"
                  >
                    <Icon
                      type="file"
                      class="mr-1"
                    />
                    <span>{{ file.name }}</span>
                  </div>
                </div>
              </ListGroupItem>
            </ListGroup>

            <Button
              v-if="$can('outcome_appointments')"
              :label="$t('ui.actions.save')"
              icon="save"
              outlined
              severity="primary"
              class="mt-4"
              :is-loading="appointmentUpdateChecklist.isLoading.value"
              @click="updateChecklistSubmit"
            />
          </CardBody>
        </Card>
      </div>

      <div>
        <Card>
          <CardBody>
            <div v-if="appointmentData.reloop">
              <div class="flex justify-between">
                <div class="flex flex-col gap-1">
                  <CardTitle
                    title="ReLoop"
                    :has-margin="false"
                  />
                  <div class="text-secondary text-sm">
                    {{ $t('ui.entities.reloop.submittedBy') }}
                    {{ appointmentData.reloop.completedBy }}
                    <span
                      v-tippy="{
                        content: dayjs(appointmentData.reloop.createdAt).format('ll HH:MM')
                      }"
                      class="underline decoration-dotted"
                    >
                      {{ dayjs().to(dayjs(appointmentData.reloop.createdAt)) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <div v-if="appointmentData.reloop.questions.find(q => q.id == 10)">
                      <Chip
                        :severity="
                          appointmentData.reloop.questions.find(q => q.id == 10)?.answer.value >= 5
                            ? 'success'
                            : 'danger'
                        "
                      >
                        {{ $t('ui.entities.reloop.satisfaction') }}:
                        {{ appointmentData.reloop.questions.find(q => q.id == 10)?.answer.value }}
                      </Chip>
                    </div>
                    <span v-if="appointmentData.reloop.questions.find(q => q.id == 13)?.answer.value == 1">
                      <Chip
                        icon="warning"
                        severity="warning"
                      >
                        {{ $t('ui.entities.reloop.alertManagement') }}
                      </Chip>
                    </span>
                  </div>
                </div>
                <div>
                  <Chip
                    size="lg"
                    :severity="
                      getScoreColor((appointmentData.reloop.score.result / appointmentData.reloop.score.possible) * 100)
                    "
                  >
                    <div class="text-sm mr-2">{{ $t('ui.entities.reloop.score') }}</div>
                    <div
                      v-if="appointmentData.reloop.score.possible > 0"
                      class="text-xl font-medium"
                    >
                      {{ appointmentData.reloop.score.result }}
                      <Icon
                        type="slash-forward"
                        class="mx-0"
                      />
                      {{ appointmentData.reloop.score.possible }}
                    </div>
                    <div v-else>N/A</div>
                  </Chip>
                </div>
              </div>

              <ListGroup class="mt-4">
                <ListGroupItem
                  v-if="reloopAttended = appointmentData.reloop.questions.find(r => r.id == 1)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopAttended.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopAttended.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div>{{ reloopAttended.shortTitle }}</div>
                </ListGroupItem>
                <ListGroupItem
                  v-if="reloopSettlementGiven = appointmentData.reloop.questions.find(r => r.id == 2)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopSettlementGiven.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopSettlementGiven.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div class="flex-grow">{{ reloopSettlementGiven.shortTitle }}</div>
                  <div v-if="reloopSettlementGiven.answer.value != 'Yes' && reloopSettlementGiven.answer.value != 'No'">
                    {{ reloopSettlementGiven.answer.value }}
                  </div>
                </ListGroupItem>
                <ListGroupItem
                  v-if="reloopValued = appointmentData.reloop.questions.find(r => r.id == 4)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopValued.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopValued.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div class="flex-grow">{{ reloopValued.shortTitle }}</div>
                  <div v-if="reloopValued.answer.value != 'Yes' && reloopValued.answer.value != 'No'">
                    {{ reloopValued.answer.value }}
                  </div>
                </ListGroupItem>
                <ListGroupItem
                  v-if="reloopTestDrive = appointmentData.reloop.questions.find(r => r.id == 6)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopTestDrive.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopTestDrive.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div class="flex-grow">{{ reloopTestDrive.shortTitle }}</div>
                  <div v-if="reloopTestDrive.answer.value != 'Yes' && reloopTestDrive.answer.value != 'No'">
                    {{ reloopTestDrive.answer.value }}
                  </div>
                </ListGroupItem>
                <ListGroupItem
                  v-if="reloopMetManagement = appointmentData.reloop.questions.find(r => r.id == 7)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopMetManagement.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopMetManagement.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div class="flex-grow">{{ reloopMetManagement.shortTitle }}</div>
                  <div v-if="reloopMetManagement.answer.value != 'Yes' && reloopMetManagement.answer.value != 'No'">
                    {{ reloopMetManagement.answer.value }}
                  </div>
                </ListGroupItem>
                <ListGroupItem
                  v-if="reloopFinalOptions = appointmentData.reloop.questions.find(r => r.id == 8)"
                  class="flex gap-2"
                >
                  <Icon
                    :type="reloopFinalOptions.answer.score == 1 ? 'check-circle' : 'circle-xmark'"
                    :class="reloopFinalOptions.answer.score == 1 ? 'text-success' : 'text-danger'"
                  />
                  <div class="flex-grow">{{ reloopFinalOptions.shortTitle }}</div>
                  <div v-if="reloopFinalOptions.answer.value != 'Yes' && reloopFinalOptions.answer.value != 'No'">
                    {{ reloopFinalOptions.answer.value }}
                  </div>
                </ListGroupItem>
              </ListGroup>

              <ListGroup class="mt-4">
                <ListGroupItem v-if="reloopDecision = appointmentData.reloop.questions.find(r => r.id == 9)">
                  <div class="flex gap-2">
                    <Icon type="thought-bubble" />
                    <div>{{ reloopDecision.shortTitle }}</div>
                  </div>
                  <div class="ml-7">{{ reloopDecision.answer.value }}</div>
                </ListGroupItem>
                <ListGroupItem v-if="reloopSettlementAmount = appointmentData.reloop.questions.find(r => r.id == 3)">
                  <div class="flex gap-2">
                    <Icon type="money-check" />
                    <div>{{ reloopSettlementAmount.shortTitle }}</div>
                  </div>
                  <div class="ml-7">
                    {{
                      formatNumber(reloopSettlementAmount.answer.value, 'currency', 2, appointmentData.outcome.currency)
                    }}
                  </div>
                </ListGroupItem>
                <ListGroupItem v-if="reloopValuation = appointmentData.reloop.questions.find(r => r.id == 5)">
                  <div class="flex gap-2">
                    <Icon type="car-side" />
                    <div>{{ reloopValuation.shortTitle }}</div>
                  </div>
                  <div class="ml-7">
                    {{ formatNumber(reloopValuation.answer.value, 'currency', 2, appointmentData.outcome.currency) }}
                  </div>
                </ListGroupItem>
                <ListGroupItem v-if="reloopFeedback = appointmentData.reloop.questions.find(r => r.id == 11)">
                  <div class="flex gap-2">
                    <Icon type="message-lines" />
                    <div>{{ reloopFeedback.shortTitle }}</div>
                  </div>
                  <div class="ml-7">{{ reloopFeedback.answer.value }}</div>
                </ListGroupItem>
                <ListGroupItem v-if="reloopBadExperience = appointmentData.reloop.questions.find(r => r.id == 12)">
                  <div class="flex gap-2">
                    <Icon type="thumbs-down" />
                    <div>{{ reloopBadExperience.shortTitle }}</div>
                  </div>
                  <div class="ml-7">{{ reloopBadExperience.answer.value }}</div>
                </ListGroupItem>
                <ListGroupItem v-if="reloopNoShowReason = appointmentData.reloop.questions.find(r => r.id == 14)">
                  <div class="flex gap-2">
                    <Icon type="user-slash" />
                    <div>{{ reloopNoShowReason.shortTitle }}</div>
                  </div>
                  <div class="ml-7">{{ reloopNoShowReason.answer.value }}</div>
                </ListGroupItem>
              </ListGroup>
            </div>

            <div v-else>
              <div class="flex justify-between items-center mb-4">
                <CardTitle
                  title="ReLoop"
                  :has-margin="false"
                />
                <div
                  v-if="dayjs().isBefore(dayjs(appointmentData.time).add(1, 'day').hour(10).minute(0).second(0))"
                  class="text-quaternary"
                >
                  Sending {{ dayjs().to(dayjs(appointmentData.time).add(1, 'day')) }}
                </div>
                <div
                  v-else-if="!props.agreementData.customer.contact.email"
                  class="text-quaternary"
                >
                  <Icon
                    type="circle-xmark"
                    class="text-error"
                  />
                  Not sent - no email address
                </div>
                <div
                  v-else-if="appointmentData.reloopEmailSentAt"
                  class="text-quaternary"
                >
                  <Icon
                    type="circle-check"
                    class="text-success"
                  />
                  Sent on {{ dayjs(appointmentData.reloopEmailSentAt).format('Do MMM YYYY') }}
                </div>
                <div
                  v-else
                  class="text-quaternary"
                >
                  <Icon
                    type="circle-xmark"
                    class="text-error"
                  />
                  Failed to send
                </div>
              </div>
              <div class="flex gap-2">
                <a
                  v-if="
                    $can('submit_reloop') &&
                    !appointmentData.isCancelled &&
                    dayjs(appointmentData.time).isBefore(dayjs())
                  "
                  :href="appointmentData?.reloopFormUrl"
                  target="_blank"
                >
                  <Button
                    :label="$t('ui.actions.new')"
                    icon="plus"
                    outlined
                    severity="secondary"
                  />
                </a>
                <!-- <Button
                    label="Re-send"
                    icon="share"
                    outlined
                    severity="secondary"
                    :is-loading="sendReloopIsLoading"
                    @click="sendReloopForm"
                  /> -->
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  </div>
  <div v-else-if="appointment.isLoading.value">
    <Skeleton
      width="100%"
      height="20rem"
      class="mb-4"
    />
    <div class="flex justify-between gap-4">
      <Skeleton
        width="50%"
        height="25rem"
        class="mb-4"
      />
      <Skeleton
        width="50%"
        height="25rem"
        class="mb-4"
      />
    </div>
  </div>
  <div
    v-else
    class="mt-32"
  >
    <Empty
      title="Not found"
      description="This appointment does not exist"
      icon="cloud-question"
    />
  </div>

  <teleport to="#modals-container">
    <AppointmentHandler
      v-if="appointmentData"
      :key="Math.random()"
      :appointment="appointmentData"
      :is-open="appointmentHandlerIsOpen"
      :agreement-contact-details="props.agreementData.customer.contact"
      :dealership-automated-comms-config="props.agreementData.dealership.automatedCommsConfig"
      @close-appointment-handler="appointmentHandlerIsOpen = false"
      @appointment-updated="rescheduleAppointmentSubmit"
    />

    <Modal
      :is-open="editNoteModalIsOpen"
      :title="$t('ui.crud.update', { item: $t('ui.common.note') })"
      :buttons="['close', 'ok']"
      ok-text="Save"
      close-text="Cancel"
      :show-loader-on-confirm="true"
      :is-confirming="appointmentNoteIsLoading"
      :disable-close="appointmentNoteIsLoading"
      min-width="400"
      @close-modal="editNoteModalIsOpen = false"
      @ok-modal="editNoteSubmit"
    >
      <FormGroup :label="$t('ui.common.note')">
        <TextArea v-model="editNoteData.note" />
        <InputError :has-error="vEditNote$.note.$error">{{ vEditNote$.note.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <FormGroup>
        <Checkbox
          v-model="editNoteData.notifyDealership"
          :label="$t('ui.actions.notifyDealership')"
        />
      </FormGroup>
    </Modal>

    <Modal
      :is-open="cancelModalIsOpen"
      min-width="350"
      icon="calendar-xmark"
      :title="$t('ui.entities.appointment.cancelAppointment')"
      description="Only cancel when the appointment is no longer required."
      :buttons="['close', 'ok']"
      :button-colors="{ close: 'secondary', ok: 'danger' }"
      ok-text="Cancel"
      close-text="Close"
      :show-loader-on-confirm="true"
      :is-confirming="cancelIsLoading"
      :disable-close="cancelIsLoading"
      @close-modal="cancelModalIsOpen = false"
      @ok-modal="cancelAppointmentSubmit"
    >
      <FormGroup :label="$t('ui.entities.appointment.cancelledBy')">
        <Chip
          :avatar="userStore.details.profilePicUrl"
          :avatar-text="userStore.details.initials"
          size="sm"
        >
          {{ userStore.details.name }}
        </Chip>
      </FormGroup>
      <FormGroup :label="$t('ui.common.reason')">
        <TextArea
          v-model="cancelAppointmentData.updateNote"
          :value="cancelAppointmentData.updateNote"
          spell-check
          :rows="3"
          :has-error="vCancel$.updateNote.$error"
        />
        <InputError :has-error="vCancel$.updateNote.$error">{{ vCancel$.updateNote.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <FormGroup label-for="notifyCustomer">
        <Checkbox
          v-model="cancelAppointmentData.notifyCustomer"
          :readonly="userStore.details.userType == 2"
          :label="$t('ui.actions.notifyCustomer')"
        ></Checkbox>
      </FormGroup>
      <FormGroup label-for="notifyDealership">
        <Checkbox
          v-model="cancelAppointmentData.notifyDealership"
          :readonly="userStore.details.userType == 2"
          :label="$t('ui.actions.notifyDealership')"
        ></Checkbox>
      </FormGroup>

      <InputError :has-error="vCancel$.updatedByUserID.$error">
        {{ vCancel$.updatedByUserID.$errors[0]?.$message }}
      </InputError>
    </Modal>

    <Modal
      :is-open="deleteModalIsOpen"
      min-width="350"
      icon="trash"
      :title="$t('ui.entities.appointment.deleteAppointment')"
      description="Only delete when the appointment is no longer required."
      :buttons="['close', 'ok']"
      :button-colors="{ close: 'secondary', ok: 'danger' }"
      ok-text="Delete"
      close-text="Close"
      :show-loader-on-confirm="true"
      :is-confirming="deleteIsLoading"
      :disable-close="deleteIsLoading"
      @close-modal="deleteModalIsOpen = false"
      @ok-modal="deleteAppointmentSubmit"
    >
      <Alert
        icon="exclamation-triangle"
        severity="error"
        title="This cannot be undone!"
        class="mb-3"
        :show-close-button="false"
        :show-dismiss-button="false"
      />
      <FormGroup :label="$t('ui.entities.appointment.deletedBy')">
        <Chip
          :avatar="userStore.details.profilePicUrl"
          :avatar-text="userStore.details.initials"
          size="sm"
        >
          {{ userStore.details.name }}
        </Chip>
      </FormGroup>
      <FormGroup :label="$t('ui.common.reason')">
        <TextArea
          v-model="deleteAppointmentData.updateNote"
          :value="deleteAppointmentData.updateNote"
          spell-check
          :rows="3"
          :has-error="vDelete$.updateNote.$error"
        />
        <InputError :has-error="vDelete$.updateNote.$error">{{ vDelete$.updateNote.$errors[0]?.$message }}</InputError>
      </FormGroup>
    </Modal>

    <Modal
      :is-open="outcomeModalIsOpen"
      min-width="550"
      icon="calendar-check"
      :title="$t('ui.entities.appointment.outcome.setOutcome')"
      description="What was the customer's decision?"
      :buttons="['close', 'ok']"
      ok-text="Submit"
      close-text="Cancel"
      :show-loader-on-confirm="true"
      :is-confirming="submitOutcomeIsLoading"
      :disable-close="submitOutcomeIsLoading"
      pin-to-top
      @close-modal="outcomeModalIsOpen = false"
      @ok-modal="submitOutcome"
    >
      <FormGroup>
        <RadioBoxed
          v-model="appOutcome.outcome"
          :options="appOutcomeSettings.outcomeOptions"
          :has-error="vOutcome$.outcome.$error"
          @option-change="outcomeSelectionChange"
        />
        <InputError :has-error="vOutcome$.outcome.$error">{{ vOutcome$.outcome.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup v-if="appOutcome.outcome && appOutcome.outcome != 'no-show'">
        <RadioTabbed
          v-model="appOutcome.decision"
          :options="applicableOutcomeDecisions"
          full-width
          class="my-2"
          :has-error="vOutcome$.decision.$error"
          @option-change="decisionSelectionChange"
        />
        <InputError :has-error="vOutcome$.decision.$error">{{ vOutcome$.decision.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.decision == 'part_exchanged'"
        :label="$t('ui.entities.appointment.outcome.newVehicleType')"
        label-for="new-vehicle-type"
      >
        <Select
          v-model="appOutcome.newVehicleType"
          mode="single"
          label-key="label"
          value-key="value"
          :searchable="false"
          :options="appOutcomeSettings.newVehicleTypes"
        />
        <InputError :has-error="vOutcome$.newVehicleType.$error">
          {{ vOutcome$.newVehicleType.$errors[0]?.$message }}
        </InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.decision == 'part_exchanged'"
        :label="$t('ui.entities.appointment.outcome.newVehicleValue')"
        label-for="new-vehicle-value"
      >
        <Input
          v-model="appOutcome.newVehicleValue"
          :icon="appointmentData.outcome.currency.toLowerCase()"
        />
        <InputError :has-error="vOutcome$.newVehicleValue.$error">
          {{ vOutcome$.newVehicleValue.$errors[0]?.$message }}
        </InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.decision == 'part_exchanged'"
        :label="$t('ui.entities.agreement.estimatedDeliveryDate')"
        label-for="new-vehicle-delivery-date"
      >
        <DatePicker
          v-model="appOutcome.newVehicleDeliveryDate"
          :has-error="vOutcome$.newVehicleDeliveryDate.$error"
        />
        <InputError :has-error="vOutcome$.newVehicleDeliveryDate.$error">
          {{ vOutcome$.newVehicleDeliveryDate.$errors[0]?.$message }}
        </InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.decision == 'refinanced'"
        :label="$t('ui.entities.financeAgreement.amountRefinanced')"
        label-for="refinanced-amount"
      >
        <Input
          v-model="appOutcome.refinancedAmount"
          :icon="appointmentData.outcome.currency.toLowerCase()"
        />
        <InputError :has-error="vOutcome$.refinancedAmount.$error">
          {{ vOutcome$.refinancedAmount.$errors[0]?.$message }}
        </InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.decision == 'refinanced'"
        :label="$t('ui.entities.financeAgreement.refinancedUntil')"
        label-for="refinanced-until"
      >
        <DatePicker
          v-model="appOutcome.refinancedEndDate"
          :has-error="vOutcome$.refinancedEndDate.$error"
        />
        <InputError :has-error="vOutcome$.refinancedEndDate.$error">
          {{ vOutcome$.refinancedEndDate.$errors[0]?.$message }}
        </InputError>
      </FormGroup>

      <FormGroup
        v-if="appOutcome.outcome && (appOutcome.decision || appOutcome.outcome == 'no-show')"
        :label="$t('ui.common.note')"
        label-for="note"
      >
        <TextArea
          v-model="appOutcome.reason"
          spell-check
          :rows="3"
        />
        <InputError :has-error="vOutcome$.reason.$error">{{ vOutcome$.reason.$errors[0]?.$message }}</InputError>
      </FormGroup>
    </Modal>

    <Modal
      title="Upload Files"
      :is-open="uploadFilesModalIsOpen"
      ok-text="Submit"
      close-text="Cancel"
      show-loader-on-confirm
      :buttons="[]"
      @close-modal="uploadFilesModalIsOpen = false"
    >
      <FileUpload
        :header-title="null"
        :api-endpoint="uploadFileApiEndpoint"
        button-position="full-width"
        @cancel-all="uploadFilesModalIsOpen = false"
        @uploaded="$nextTick(() => fetchAppointment())"
      />
    </Modal>
  </teleport>
</template>

<script setup>
// vue
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// components
import Alert from '@/components/alert/Alert.vue'
import Button from '@/components/button/Button.vue'
import CopyText from '@/components/CopyText.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Chip from '@/components/chip/Chip.vue'
import DateTile from '@/components/date-tile/DateTile.vue'
import Empty from '@/components/empty/Empty.vue'
import FileUpload from '@/components/file-upload/FileUpload.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Label from '@/components/forms/Label.vue'
import RadioBoxed from '@/components/forms/RadioBoxed.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Select from '@/components/forms/Select.vue'
import TextArea from '@/components/forms/TextArea.vue'
import Icon from '@/components/icon/Icon.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Modal from '@/components/modal/Modal.vue'
import Separator from '@/components/separator/Separator.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import AppointmentHandler from '@/components/unique/appointments/AppointmentHandler.vue'
// composables
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useUserStore } from '@/stores/UserStore.ts'
// utilities
import { getDictionaryItem } from '@/dictionary'
import { alertToast } from '@/utilities/notification'
// validators
import { useAbility } from '@casl/vue'
import useVuelidate from '@vuelidate/core'
import { helpers, maxLength, numeric, required, requiredIf } from '@vuelidate/validators'
// libraries
import dayjs from 'dayjs'
// utilities
import { formatNumber } from '@/utilities/number'

const props = defineProps({
  agreementData: {
    type: Object,
    default: null
  }
})
const emit = defineEmits(['appointmentUpdated'])
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { can } = useAbility()

const appointmentDetailsVisible = ref(false)
const appointmentDetailsCollapsable = ref(false)

const updateSource = userStore.details.userType == 1 ? 'retain' : 'dealer'

const appointment = useApiRequest()
const appointmentData = ref(null)
const appointmentCancel = useApiRequest()
const deleteAppointmentRequest = useApiRequest()
const appointmentAssign = useApiRequest()
const appointmentUpdateNote = useApiRequest()
const appointmentUpdateChecklist = useApiRequest()
const appointmentSubmitOutcome = useApiRequest()

const assignableUsers = useApiRequest()

const appointmentHandlerIsOpen = ref(false)

const appChecklist = ref({
  settlementFigureGiven: false,
  settlementAmount: null,
  vehicleValued: false,
  valuationAmount: null,
  testDriveCompleted: false,
  newCarOffersProvided: false,
  refinanceQuoteGiven: false,
  refinanceQuoteAmount: null
})

function fetchAppointment() {
  appointment.send({ endpoint: '/v1/appointments/' + route.params.id, method: 'GET' }).then(response => {
    if (response?.data?.response || !response?.success) {
      return
    }

    appointmentData.value = response.data

    // Get all dealers for this dealership
    assignableUsers
      .send({
        endpoint:
          '/v1/users?showDisabled=true&forAppointmentAssignment=true&userType=externalUsers&dealerships=' +
          appointmentData.value.dealershipID,
        method: 'GET'
      })
      .then(() => {
        if (assignableUsers.value && assignableUsers.data.value.length) {
          assignableUsers.value = assignableUsers.data.value
            .filter(user => {
              // Update 'disabled' property
              user.disabled = !user.isActive

              // Filter out disabled users, unless it's the one assigned to
              return !user.disabled || user.id === appointmentData.value.assignedTo.id
            })
            .sort((a, b) => a.forename.localeCompare(b.forename))
        }

        if (appointmentData.value.assignedTo && appointmentData.value.assignedTo > 0) {
          const isWithinAssignableUsers = assignableUsers.data.value.find(
            user => user.id === appointmentData.value.assignedTo.id
          )

          if (!isWithinAssignableUsers) {
            assignableUsers.data.value.push({
              id: appointmentData.value.assignedTo.id,
              fullName: appointmentData.value.assignedTo.forename + ' ' + appointmentData.value.assignedTo.surname,
              disabled: true
            })
          }

          const assignedUser = assignableUsers.data.value.find(obj => obj.id == appointmentData.value.assignedTo.id)
          appointmentData.value.assignedTo.id = assignedUser.id
        }

        // Set appChecklist values from appointment.checklist object as a copy, not a reference
        appChecklist.value = JSON.parse(JSON.stringify(appointmentData.value.checklist))

        // Set individual outcome values from appointment.outcome object as a copy, not a reference
        const outcome = JSON.parse(JSON.stringify(appointmentData.value.outcome))
        appOutcome.value.outcome = outcome.outcome ?? null
        appOutcome.value.decision = outcome.decision ?? null
        appOutcome.value.newVehicleType = outcome.newVehicle?.type ?? null
        appOutcome.value.newVehicleValue = outcome.newVehicle?.value ?? null
        appOutcome.value.newVehicleDeliveryDate = outcome.newVehicle?.estimatedDeliveryDate ?? null
        appOutcome.value.refinancedEndDate = outcome.refinancedUntil ?? null
        appOutcome.value.reason = outcome.reason ?? null

        // If appointment is in the past, set appointmentDetailsCollapsable to true
        if (dayjs().isAfter(dayjs(appointmentData.value.time))) {
          appointmentDetailsCollapsable.value = true
        } else {
          appointmentDetailsCollapsable.value = false
          appointmentDetailsVisible.value = true
        }
      })
  })
}

// Only fetch appointment data when component is mounted
onMounted(() => {
  fetchAppointment()
})

const isActionable = computed(() => {
  // get the appointment time
  const appointmentTime = dayjs(appointmentData.value.time)

  // check if the current time is within an hour of the appointment time
  return dayjs().isAfter(appointmentTime.subtract(1, 'hour'))
})

const cancelModalIsOpen = ref(false)
const cancelIsLoading = ref(false)

const cancelAppointmentData = ref({
  notifyCustomer: true,
  notifyDealership: true,
  updatedByUserID: userStore.details.id,
  updateSource: updateSource,
  updateNote: null
})

const appointmentCancelRules = {
  updateNote: { required },
  updatedByUserID: { required }
}

const vCancel$ = useVuelidate(appointmentCancelRules, cancelAppointmentData)

async function cancelAppointmentSubmit() {
  const isFormCorrect = await vCancel$.value.$validate()
  if (!isFormCorrect) {
    console.error(vCancel$.value.$errors)
    return
  }

  cancelIsLoading.value = true
  // Make this part of the function wait for the response from the API
  const response = await appointmentCancel.send({
    endpoint: '/v1/appointments/' + route.params.id + '/cancel',
    method: 'PATCH',
    data: cancelAppointmentData.value
  })

  if (!response.success) {
    alertToast('Error!', response.message, 'error')
  }
  if (response.success) {
    cancelModalIsOpen.value = false

    // Make sure the Appointment ID is received in the response
    if (!response.data) {
      alertToast('No Data returned. Contact support', null, 'error')
    } else {
      // Fetch the appointment data again // TODO: change this to be more efficient and not fetching the entire appointment again.
      fetchAppointment()

      // If response has a message, means there was an issue.
      if (response.message) {
        alertToast('Cancelled', response.message, 'warning')
      } else {
        alertToast('Cancelled', null, 'success')
      }
    }
  }
  cancelIsLoading.value = false
}

function cancelClick() {
  cancelModalIsOpen.value = true
}

const deleteModalIsOpen = ref(false)
const deleteIsLoading = ref(false)

const deleteAppointmentData = ref({
  notifyCreator: true,
  notifyAssignedUser: true,
  updateSource: updateSource,
  updateNote: null
})

const appointmentDeleteRules = {
  updateNote: {
    required: requiredIf(() => cancelAppointmentData.value.updateSource == 'retain')
  }
}

const vDelete$ = useVuelidate(appointmentDeleteRules, deleteAppointmentData)

function deleteClick() {
  deleteModalIsOpen.value = true
}

async function deleteAppointmentSubmit() {
  const isFormCorrect = await vDelete$.value.$validate()
  if (!isFormCorrect) {
    console.error(vDelete$.value.$errors)
    return
  }

  deleteIsLoading.value = true
  // Make this part of the function wait for the response from the API
  try {
    deleteAppointmentRequest
      .send({
        method: 'DELETE',
        endpoint: '/v1/appointments/' + route.params.id,
        data: deleteAppointmentData.value
      })
      .then(response => {
        console.error('delete appt response', response)

        if (response.success) {
          deleteModalIsOpen.value = false
          emit('appointmentUpdated', appointmentData.value)
          router.push('/agreement/' + route.params.agid + '/appointments')
          alertToast('Deleted', null, 'success')
        } else {
          alertToast('Failed to delete appointment', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to delete appointment', err.message, 'error')
  }
}

function rescheduleAppointmentSubmit() {
  appointmentHandlerIsOpen.value = false
  fetchAppointment()
}

function rescheduleClick() {
  appointmentHandlerIsOpen.value = true
}

function getScoreColor(score) {
  if (score <= 70) {
    return 'danger'
  } else if (score <= 80) {
    return 'warning'
  } else {
    return 'success'
  }
}

const appointmentAssigning = appointmentAssign.isLoading
function updateAssignedUser(assignTo) {
  // console.log(appointmentData.value.assignedTo, assignTo)
  // if (appointmentData.value.assignedTo == assignTo) {
  //   console.log('yes')

  //   return
  // }

  if (assignableUsers.data.value.find(user => user.disabled == true)) {
    assignableUsers.data.value = assignableUsers.data.value.filter(user => user.disabled == false)
  }

  assignTo = assignTo ?? null
  const dataToPost = {
    userToAssign: assignTo.id,
    notifyUser: false,
    updatedBy: userStore.details.id,
    updateSource: updateSource
  }

  // Make this part of the function wait for the response from the API
  appointmentAssign
    .send({
      endpoint: '/v1/appointments/' + route.params.id + '/assign',
      method: 'PATCH',
      data: dataToPost
    })
    .then(response => {
      if (response.success) {
        appointmentData.value.status = response.data.status
        alertToast('Assigned', null, 'success')
      } else {
        alertToast('Failed to assign appointment', response.message, 'error')
      }
    })
}

const editNoteModalIsOpen = ref(false)

const editNoteData = ref({
  note: null,
  notifyDealership: false
})

const editNoteVuelidateRules = {
  note: { required },
  notifyDealership: {}
}
const vEditNote$ = useVuelidate(editNoteVuelidateRules, editNoteData, {
  $stopPropagation: true
})

const appointmentNoteIsLoading = ref(false)

async function editNoteSubmit() {
  const currentNote = appointmentData.value.note
  if (currentNote == editNoteData.value.note) {
    editNoteModalIsOpen.value = false
    return
  }

  const isFormCorrect = await vEditNote$.value.$validate()

  if (!isFormCorrect) {
    return
  }
  const dataToPost = {
    note: editNoteData.value.note,
    notifyDealership: editNoteData.value.notifyDealership,
    updatedByUserId: userStore.details.id,
    updateSource: updateSource
  }

  appointmentNoteIsLoading.value = true
  // Make this part of the function wait for the response from the API
  appointmentUpdateNote
    .send({
      endpoint: '/v1/appointments/' + route.params.id + '/update-note',
      method: 'PATCH',
      data: dataToPost
    })
    .then(response => {
      if (response.success) {
        // Make sure the Appointment ID is received in the response
        if (!response.data) {
          alertToast('No Data returned', 'Contact support', 'error')
        } else {
          // If response has a message, means there was an issue.
          if (response.message) {
            alertToast('Appointment Note Updated', response.message, 'warning')
          } else {
            alertToast('Appointment Note Updated', null, 'success')
          }
          editNoteModalIsOpen.value = false
          appointmentData.value.note = response.data.note
        }
      } else {
        alertToast('failed to update', response.message, 'error')
      }
      appointmentNoteIsLoading.value = false
    })
}

const appointmentChecklistRules = {
  settlementAmount: {
    numeric: helpers.withMessage('Invalid number', numeric)
  },
  valuationAmount: {
    numeric: helpers.withMessage('Invalid number', numeric)
  },
  refinanceQuoteAmount: {
    numeric: helpers.withMessage('Invalid number', numeric)
  }
}

const checklistDataToPost = ref(null)
const vChecklist$ = useVuelidate(appointmentChecklistRules, checklistDataToPost, {
  $stopPropagation: true
})

async function updateChecklistSubmit() {
  // remove commas before vuelidate check
  checklistDataToPost.value = {
    ...appChecklist.value,
    refinanceQuoteAmount:
      appChecklist.value.refinanceQuoteAmount != null
        ? appChecklist.value.refinanceQuoteAmount.replace(/[,]/g, '')
        : null,
    settlementAmount:
      appChecklist.value.settlementAmount != null ? appChecklist.value.settlementAmount.replace(/[,]/g, '') : null,
    valuationAmount:
      appChecklist.value.valuationAmount != null ? appChecklist.value.valuationAmount.replace(/[,]/g, '') : null
  }

  const isFormCorrect = await vChecklist$.value.$validate()

  if (!isFormCorrect) {
    return
  }

  // convert empty strings into null
  for (const key in checklistDataToPost.value) {
    if (checklistDataToPost.value[key] === '') {
      checklistDataToPost.value[key] = null
    }
  }

  appointmentUpdateChecklist
    .send({
      endpoint: '/v1/appointments/' + route.params.id + '/checklist',
      method: 'PATCH',
      data: checklistDataToPost.value
    })
    .then(response => {
      if (response.success) {
        // If response has a message, means there was an issue.
        if (response.message) {
          alertToast('Checklist Updated', response.message, 'warning')
        } else {
          alertToast('Checklist Updated', null, 'success')
        }
      } else {
        alertToast(response.message, 'error')
      }
    })
}

const appOutcomeSettings = ref({
  outcomeOptions: [
    {
      label: 'No-Show',
      value: 'no-show',
      icon: 'ban',
      description: "Customer didn't attend",
      classes: 'col-span-2'
    },
    {
      label: 'Completed',
      value: 'completed',
      icon: 'check',
      description: 'Decision made',
      classes: 'col-span-2'
    },
    {
      label: 'Ongoing',
      value: 'ongoing',
      icon: 'arrows-rotate',
      description: 'No decision was made',
      classes: 'col-span-2'
    }
  ],
  decisionOptions: [
    {
      label: 'Part-Exchanged',
      value: 'part_exchanged',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['PCP', 'HP']
    },
    {
      label: 'Refinanced',
      value: 'refinanced',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['PCP']
    },
    {
      label: 'Settled',
      value: 'settled',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['PCP']
    },
    {
      label: 'Handed Back',
      value: 'handed_back',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
    },
    {
      label: 'Buy Back',
      value: 'buy_back',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['PCP']
    },
    {
      label: 'New Lease',
      value: 'new_lease',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['CH', 'PCH', 'BCH']
    },
    {
      label: 'Extended',
      value: 'extended',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['CH', 'PCH', 'BCH']
    },
    {
      label: 'Keeping',
      value: 'keeping',
      applicableOutcomeTypes: ['completed'],
      applicableContractTypes: ['HP']
    },
    {
      label: 'Undecided',
      value: 'undecided',
      applicableOutcomeTypes: ['ongoing'],
      applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
    },
    {
      label: 'Not Ready',
      value: 'not_ready',
      applicableOutcomeTypes: ['ongoing'],
      applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
    },
    {
      label: 'Poor Offer',
      value: 'poor_offer',
      applicableOutcomeTypes: ['ongoing'],
      applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
    }
  ],
  newVehicleTypes: [
    { value: 'hatchback', label: 'Hatchback' },
    { value: 'estate', label: 'Estate' },
    { value: 'crossover', label: 'Crossover' },
    { value: 'suv', label: 'SUV' },
    { value: 'motorcycle', label: 'Motorcycle' }
  ]
})

const appOutcome = ref({
  outcome: appointmentData.value?.outcome.outcome ?? null,
  decision: appointmentData.value?.outcome.decision ?? null,
  newVehicleType: appointmentData.value?.outcome.newVehicle?.type ?? null,
  newVehicleValue: appointmentData.value?.outcome.newVehicle?.value ?? null,
  newVehicleDeliveryDate: appointmentData.value?.outcome.newVehicle?.deliveryDate ?? null,
  refinancedAmount: appointmentData.value?.outcome.refinanced?.amount ?? null,
  refinancedEndDate: appointmentData.value?.outcome.refinanced?.until ?? null,
  reason: appointmentData.value?.outcome.reason ?? null
})

const appointmentOutcomeRules = {
  outcome: { required },
  decision: {
    required: requiredIf(() => appOutcome.value.outcome != 'no-show')
  },
  newVehicleType: {},
  newVehicleValue: { numeric },
  newVehicleDeliveryDate: {},
  refinancedAmount: { numeric },
  refinancedEndDate: {},
  reason: { maxLength: maxLength(200) }
}

function outcomeSelectionChange(option) {
  appOutcome.value.outcome = option
  appOutcome.value.decision = null
}

// Computed value which sets the outcome decisions based on the outcome selection, and the appointmentData.financeProductType
const applicableOutcomeDecisions = computed(() => {
  const applicableOutcomeDecisions = appOutcomeSettings.value.decisionOptions.filter(option => {
    return (
      option.applicableOutcomeTypes.includes(appOutcome.value.outcome) &&
      option.applicableContractTypes.includes(appointmentData.value.financeProductType)
    )
  })
  if (applicableOutcomeDecisions.length === 0) {
    return appOutcomeSettings.value.decisionOptions.filter(option => {
      return option.applicableOutcomeTypes.includes(appOutcome.value.outcome)
    })
  }
  return applicableOutcomeDecisions
})

const outcomeModalIsOpen = ref(false)
const submitOutcomeIsLoading = ref(false)
const vOutcome$ = useVuelidate(appointmentOutcomeRules, appOutcome, {
  $stopPropagation: true
})

async function submitOutcome() {
  const isFormCorrect = await vOutcome$.value.$validate()

  if (!isFormCorrect) {
    return
  }
  const dataToPost = {
    outcome: appOutcome.value.outcome,
    decision: appOutcome.value.decision,
    partExchanged: {
      type: appOutcome.value.newVehicleType,
      value: appOutcome.value.newVehicleValue,
      estimatedDeliveryDate: appOutcome.value.newVehicleDeliveryDate
    },
    refinanced: {
      amount: appOutcome.value.refinancedAmount,
      until: appOutcome.value.refinancedEndDate
    },
    note: appOutcome.value.reason,
    updatedBy: userStore.details.id,
    updatedByType: updateSource
  }

  submitOutcomeIsLoading.value = true
  appointmentSubmitOutcome
    .send({
      endpoint: '/v1/appointments/' + route.params.id + '/outcome',
      method: 'PUT',
      data: dataToPost
    })
    .then(response => {
      if (response.success) {
        // If response has a message, means there was an issue.
        if (response.message) {
          alertToast('Appointment Outcome Updated', response.message, 'warning')
        } else {
          alertToast('Appointment Outcome Updated', null, 'success')
        }
        outcomeModalIsOpen.value = false
        // OPTIMIZE: Update the appointment data without fetching the entire appointment again
        fetchAppointment()
      } else {
        alertToast('Failed to Update', response.message, 'error')
      }
      submitOutcomeIsLoading.value = false
    })
}

const appointmentIsLocked = computed(() => {
  let reason = null

  if (can('edit_locked_appointments')) return reason

  if (
    dayjs(appointmentData.value?.time).isBefore(dayjs().subtract(7, 'days')) &&
    appointmentData.value?.outcome.outcome
  ) {
    reason = 'Appointment is locked as it is older than 7 days and an outcome has been set.'
  } else if (appointmentData.value?.isCancelled) {
    reason = 'Appointment is locked as it has been cancelled.'
  }

  return reason
})

const uploadFileApiEndpoint = ref(null)
const uploadFilesModalIsOpen = ref(false)

function uploadChecklistFileClick(category) {
  uploadFileApiEndpoint.value = `${import.meta.env.VITE_API_BASE_URL}/v1/appointments/${route.params.id}/file-upload`
  switch (category) {
    case 'settlement':
      uploadFileApiEndpoint.value = uploadFileApiEndpoint.value + '?cat=settlement'
      break
    case 'newCarOffers':
      uploadFileApiEndpoint.value = uploadFileApiEndpoint.value + '?cat=new-car-offers'
      break
    case 'refinanceQuote':
      uploadFileApiEndpoint.value = uploadFileApiEndpoint.value + '?cat=refinance-quote'
      break

    default:
      break
  }

  uploadFilesModalIsOpen.value = true
}

function openFileInNewTab(file) {
  window.open(file.url, '_blank')
}

// TODO: Send Reloop to customer here
// const sendReloopIsLoading = ref(false)
// function sendReloopForm() {
//   sendReloopIsLoading.value = true
//   let customerEmail = props.agreementData.customer.contact.email

//   sendReloopIsLoading.value = false
// }
</script>
